/* Qué reserva va con qué parada. La hoja de reservas y el itinerario no escriben igual los nombres
   («Todai-ji · Gran Buda» frente a «Tōdai-ji»), así que se comparan las palabras sin tildes ni macrones
   y la hora. Las que llegan al día siguiente de madrugada o tras un vuelo (Haneda → Kansai el día 2 a
   las 13:40) se buscan también en los bloques del día anterior pasados de medianoche. */
import type { Bloque, Reserva, Viaje } from './tipos.ts';

/** Palabras que casi todo comparte: cuentan poco. */
const COMUNES = new Set(['osaka', 'tokyo', 'tokio', 'kyoto', 'kioto', 'station', 'airport', 'shin', 'personas', 'sesion',
  'principal', 'visita', 'nocturna', 'hotel', 'para', 'con', 'del', 'los', 'las', 'por', 'una', 'apartment', 'japan', 'madrid',
  'david', 'yago', 'daniel']);

export const sinTildes = (s: string) => s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export function palabras(s: string): Set<string> {
  const t = sinTildes(s).replace(/-(ji|jo|dera|en)\b/g, '$1')    // Todai-ji ≈ Todaiji, Nijo-jo ≈ Nijojo
    .replace(/([a-z0-9])([\u3040-\u30ff\u4e00-\u9fff])/g, '$1 $2');  // AARON通天西 → aaron 通天西
  return new Set(t.split(/[^a-z0-9\u3040-\u30ff\u4e00-\u9fff]+/).filter(w => w.length >= 3));
}

const peso = (w: string) => (COMUNES.has(w) ? 0.3 : 1);
const comparte = (a: Set<string>, b: Set<string>) => [...a].reduce((s, w) => s + (b.has(w) ? peso(w) : 0), 0);
const minuto = (h?: string | null) => { const m = h?.match(/(\d{1,2}):(\d{2})/); return m ? +m[1] * 60 + +m[2] : null; };
const cancelada = (r: Reserva) => /CANCEL/.test(r.estado ?? '');

export interface Enlaces { porBloque: Map<string, Reserva[]>; sueltas: Reserva[] }

const memo = new WeakMap<Viaje, Enlaces>();

/** Cada reserva (no cancelada) con su parada más parecida; las que no casan con ninguna quedan sueltas. */
export function enlaces(v: Viaje): Enlaces {
  const hecho = memo.get(v);
  if (hecho) return hecho;
  const porBloque = new Map<string, Reserva[]>(), sueltas: Reserva[] = [];
  for (const r of v.reservas) {
    if (cancelada(r) || !r.dia) continue;
    const pr = palabras(r.que), hr = minuto(r.inicio);
    const candidatos: [Bloque, number][] = [
      ...(v.dias[r.dia - 1]?.bloques ?? []).map(b => [b, 0] as [Bloque, number]),
      ...(v.dias[r.dia - 2]?.bloques ?? []).filter(b => (b.fin ?? b.ini ?? 0) > 1440).map(b => [b, 1440] as [Bloque, number]),
    ];
    let mejor: Bloque | null = null, nota = 0;
    for (const [b, des] of candidatos) {
      const texto = comparte(pr, palabras(b.que)) + 0.5 * comparte(pr, palabras(b.plan));
      if (texto === 0) continue;
      let hora = 0;
      if (hr != null && b.ini != null) {
        const m = hr + des, fin = b.fin ?? b.ini + 60;
        hora = m === b.ini ? 2 : m >= b.ini - 20 && m < fin ? 1 : -0.5;
      }
      // Un alojamiento va con su check-in, no con cualquier bloque que nombre el hotel
      if (r.tipo === 'alojamiento') hora += /check-?in/i.test(b.que) ? 1.5 : b.tipo === 'alojamiento' ? 0.5 : -1;
      const total = texto + hora;
      if (total > nota) { nota = total; mejor = b; }
    }
    // Un hotel que el itinerario no nombra va con el check-in de ese día, si lo hay
    if (!mejor && r.tipo === 'alojamiento') {
      mejor = v.dias[r.dia - 1]?.bloques.find(b => /check-?in/i.test(b.que)) ?? null;
      nota = mejor ? 1 : 0;
    }
    if (mejor && nota >= 1) {
      const l = porBloque.get(mejor.id) ?? [];
      l.push(r);
      porBloque.set(mejor.id, l);
    } else sueltas.push(r);
  }
  const e = { porBloque, sueltas };
  memo.set(v, e);
  return e;
}

export const reservasDe = (v: Viaje, bloque: string) => enlaces(v).porBloque.get(bloque) ?? [];
